import { type CSSProperties } from 'react';
import PictureImage from '../Common/PictureImage';

import { Link } from 'react-router-dom';
import Canonical from '../../seo/Canonical';
import '../Logistics/Logistics.css';
import './FulfilmentCenter.css';
import LogisticsPageHero from '../Logistics/LogisticsPageHero';
import Newsletter from '../Homepage/Newsletter';
import Footer from '../Layouts/Footer';
import { useRevealClass } from '../../hooks/useInViewOnce';

const HERO_IMAGE = '/Images/our-fulfilment-center.webp';

const services = [
  { id: 1, title: 'Pick & Pack', copy: 'Lightning-fast order assembly with 99.9% barcode accuracy.', icon: '📦' },
  { id: 2, title: 'WMS Inventory', copy: 'Real-time stock sync across every sales channel you use.', icon: '📊' },
  { id: 3, title: 'Returns Processing', copy: 'Same-day inspection, QC and restock workflows.', icon: '↩️' },
  { id: 4, title: 'Same-Day Dispatch', copy: 'Major carriers — most orders out within 24 hours.', icon: '🚚' },
];

const workflow = [
  {
    id: 1,
    title: 'Inbound receive',
    desc: 'Inspection, labelling, quality control, WMS intake.',
    image: '/Images/Inventory_inspection.webp',
    progress: 25,
  },
  {
    id: 2,
    title: 'Dynamic slotting',
    desc: 'Velocity-based locations for faster picks.',
    image: '/Images/WMS.webp',
    progress: 50,
  },
  {
    id: 3,
    title: 'Order wave planning',
    desc: 'Batch picking by zone and carrier cut-off.',
    image: '/Images/OPTIMIZED-CASE-PACKAGING.webp',
    progress: 75,
  },
  {
    id: 4,
    title: 'Dispatch ready',
    desc: 'Labelled, packed and handed to your carrier.',
    image: '/Images/FNSKU_LABELING.webp',
    progress: 100,
  },
];

export default function FulfilmentCenter() {
  const introRef = useRevealClass('fc-reveal-visible', { rootMargin: '0px 0px -40px 0px' });
  const servicesRef = useRevealClass('fc-reveal-visible', { rootMargin: '0px 0px -40px 0px' });
  const workflowRef = useRevealClass('fc-reveal-visible', { rootMargin: '0px 0px -40px 0px' });
  const galleryRef = useRevealClass('fc-reveal-visible', { rootMargin: '0px 0px -40px 0px' });

  return (
    <div className="logistics-page">
      <Canonical baseUrl="https://www.pickpackpro.co.uk" />
      <LogisticsPageHero
        className="fc-hero-animate"
        ariaLabel="Our fulfilment centre UK"
        imageSrc={HERO_IMAGE}
        imageAlt="Pick Pack Pro fulfilment centre Milton Keynes — UK 3PL warehouse"
        titleLight="Our Fulfilment"
        titleAccent="Centre UK"
        description={
          <p>
            Scale from our 10K+ sq ft Milton Keynes 3PL — WMS-powered pick &amp; pack, 99.9% accuracy and
            same-day dispatch across channels.
          </p>
        }
        primaryCta={{ label: 'Get your quote', to: '/contact-fulfillment-services' }}
        secondaryCta={{ label: 'WMS integrations', to: '/wms-integrations' }}
      />

      {/* Introduction */}
      <section className="fc-section" ref={introRef}>
        <div className="container">
          <header className="fc-section-header fc-animate" style={{ '--order': 0 } as CSSProperties}>
            <p className="logistics-services-eyebrow">Milton Keynes HQ</p>
            <h2 className="logistics-services-title">Complete 3PL for growing brands</h2>
            <p className="logistics-services-intro fc-intro-narrow">
              Enterprise WMS, scanner-verified picking, optimal carrier routing and a team obsessed with accuracy — so
              you stay focused on revenue, not warehouse ops.
            </p>
          </header>
          <div className="fc-stat-row">
            {[
              { v: '10K+', l: 'Sq ft facility' },
              { v: '99.9%', l: 'Pick accuracy' },
              { v: '24h', l: 'Typical dispatch' },
            ].map((s, i) => (
              <div
                key={s.l}
                className="fc-stat-pill fc-animate"
                style={{ '--order': i + 1 } as CSSProperties}
              >
                <span className="fc-stat-pill__value">{s.v}</span>
                <span className="fc-stat-pill__label">{s.l}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Core services */}
      <section className="fc-section fc-section-alt" ref={servicesRef}>
        <div className="container">
          <header className="fc-section-header fc-animate" style={{ '--order': 0 } as CSSProperties}>
            <p className="logistics-services-eyebrow">What we run</p>
            <h2 className="logistics-services-title">Core fulfilment capabilities</h2>
          </header>
          <div className="fc-grid">
            {services.map((service, i) => (
              <article
                key={service.id}
                className="fc-card fc-animate"
                style={{ '--order': i + 1 } as CSSProperties}
              >
                <div className="fc-icon" aria-hidden>
                  {service.icon}
                </div>
                <h3>{service.title}</h3>
                <p>{service.copy}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Workflow */}
      <section className="fc-section fc-section-alt" ref={workflowRef}>
        <div className="container">
          <header className="fc-section-header fc-animate" style={{ '--order': 0 } as CSSProperties}>
            <p className="logistics-services-eyebrow">End-to-end</p>
            <h2 className="logistics-services-title">Your order journey in four steps</h2>
          </header>
          <div className="fc-workflow-grid">
            {workflow.map((step, i) => (
              <article
                key={step.id}
                className="fc-workflow-card fc-animate"
                style={{ '--order': i + 1 } as CSSProperties}
              >
                <div className="fc-workflow-card__top">
                  <span className="fc-step-number">{step.id}</span>
                  <div>
                    <h3>{step.title}</h3>
                    <p>{step.desc}</p>
                  </div>
                </div>
                <div className="fc-workflow-card__media">
                  <PictureImage src={step.image} alt={step.title} loading="lazy" decoding="async" />
                </div>
                <div className="fc-progress" role="presentation">
                  <div
                    className="fc-progress-bar"
                    style={{ '--fc-progress': `${step.progress}%` } as CSSProperties}
                  />
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Facility gallery */}
      <section className="fc-section" ref={galleryRef}>
        <div className="container">
          <header className="fc-section-header fc-animate" style={{ '--order': 0 } as CSSProperties}>
            <p className="logistics-services-eyebrow">Inside the warehouse</p>
            <h2 className="logistics-services-title">State-of-the-art facility</h2>
          </header>
          <div className="fc-gallery">
            <Link to="/fulfilment-facility/wms-control-centre" className="fc-gallery-item fc-animate" style={{ '--order': 1 } as CSSProperties}>
              <img src="/Images/WMS.webp" alt="WMS control centre" className="fc-gallery-img" loading="lazy"  width="800" height="600" />
              <div className="fc-gallery-overlay">
                <span>WMS control centre</span>
              </div>
              <div className="fc-gallery-caption">Live inventory dashboard</div>
            </Link>

            <Link to="/fulfilment-facility/quality-assurance" className="fc-gallery-item fc-animate" style={{ '--order': 2 } as CSSProperties}>
              <img src="/Images/Inventory_inspection.webp" alt="Quality assurance" className="fc-gallery-img" loading="lazy"  width="800" height="600" />
              <div className="fc-gallery-overlay">
                <span>Quality assurance</span>
              </div>
              <div className="fc-gallery-caption">Inbound inspection zone</div>
            </Link>

            <Link to="/fulfilment-facility/pick-pack-station" className="fc-gallery-item fc-animate" style={{ '--order': 3 } as CSSProperties}>
              <img src="/Images/OPTIMIZED-CASE-PACKAGING.webp" alt="Pick and pack station" className="fc-gallery-img" loading="lazy"  width="800" height="600" />
              <div className="fc-gallery-overlay">
                <span>Optimised packing</span>
              </div>
              <div className="fc-gallery-caption">Pick &amp; pack station</div>
            </Link>

            <Link to="/fulfilment-facility/amazon-fba-prep" className="fc-gallery-item fc-animate" style={{ '--order': 4 } as CSSProperties}>
              <img src="/Images/FNSKU_LABELING.webp" alt="Amazon FBA prep" className="fc-gallery-img" loading="lazy"  width="800" height="600" />
              <div className="fc-gallery-overlay">
                <span>Precision labelling</span>
              </div>
              <div className="fc-gallery-caption">Amazon FBA prep area</div>
            </Link>

            <Link to="/fulfilment-facility/polybagging-protection" className="fc-gallery-item fc-animate" style={{ '--order': 5 } as CSSProperties}>
              <img src="/Images/PROTECTOVE-PREP-AND-POLY-BAGING.webp" alt="Protective packaging" className="fc-gallery-img" loading="lazy"  width="800" height="600" />
              <div className="fc-gallery-overlay">
                <span>Protective packaging</span>
              </div>
              <div className="fc-gallery-caption">Polybagging &amp; protection</div>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
     

      <Newsletter />
      <Footer />
    </div>
  );
}
